import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShoppingBag, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Wishlist = () => {
  const { token } = useAuth();
  const { addToCart } = useCart();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWishlist();
  }, []);

  const fetchWishlist = async () => {
    try {
      const response = await axios.get(`${API}/wishlist`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(response.data);
    } catch (error) {
      setWishlist([]);
      toast.error('No se pudieron cargar los favoritos');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price) + ' MXN';
  };

  const handleRemove = async (productId) => {
    try {
      await axios.delete(`${API}/wishlist/remove/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWishlist(wishlist.filter(p => p.id !== productId));
      toast.success('Eliminado de favoritos');
    } catch (error) {
      toast.error('Error al eliminar de favoritos');
    }
  };

  const handleAddToCart = async (productId) => {
    try {
      await addToCart(productId, 1);
      toast.success('Agregado al carrito');
    } catch (error) {
      toast.error('Error al agregar al carrito');
    }
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#FAFAF9]">
        <div className="container-luxury py-16">
          <div className="flex items-center justify-center">
            <div className="w-12 h-12 border-2 border-[#BA7B7C] border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#FAFAF9]" data-testid="wishlist-page">
      <div className="container-luxury py-8 md:py-16">
        <h1 
          className="text-4xl md:text-5xl font-light tracking-tight text-[#1C1917] mb-12"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          Mis Favoritos
        </h1>

        {wishlist.length === 0 ? (
          <div className="text-center py-20">
            <Heart className="w-16 h-16 mx-auto text-[#E7E5E4] mb-6" />
            <p className="text-lg text-[#A8A29E] mb-8">Tu lista de favoritos está vacía</p>
            <Link to="/shop" className="btn-primary" data-testid="explore-products-btn">
              Explorar productos
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8" data-testid="wishlist-grid">
            {wishlist.map((product) => (
              <div 
                key={product.id} 
                className="product-card group"
                data-testid={`wishlist-item-${product.id}`}
              >
                {/* Image Container */}
                <div className="product-image-container bg-[#F5F0EB] aspect-[3/4] mb-4 relative">
                  <Link to={`/product/${product.id}`}>
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="product-image w-full h-full object-cover"
                    />
                  </Link>
                  
                  {/* Remove Button */}
                  <button
                    onClick={() => handleRemove(product.id)}
                    className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-red-50 transition-all shadow-soft group/btn"
                    data-testid={`remove-wishlist-${product.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-[#57534E] group-hover/btn:text-red-500 transition-colors" />
                  </button>

                  {/* Add to Cart Button */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 flex items-end justify-center pb-6 opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => handleAddToCart(product.id)}
                      className="bg-white text-[#1C1917] px-6 py-3 rounded-full text-sm tracking-wide hover:bg-[#BA7B7C] hover:text-white transition-all transform translate-y-4 group-hover:translate-y-0 flex items-center space-x-2"
                      data-testid={`add-to-cart-${product.id}`}
                    >
                      <ShoppingBag className="w-4 h-4" />
                      <span>Agregar</span>
                    </button>
                  </div>
                </div>

                {/* Product Info */}
                <Link to={`/product/${product.id}`}>
                  <p className="text-xs tracking-[0.15em] uppercase text-[#A8A29E]">
                    {product.category}
                  </p>
                  <h3 
                    className="text-lg font-light text-[#1C1917] group-hover:text-[#BA7B7C] transition-colors mt-1"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}
                  >
                    {product.name}
                  </h3>
                  <p className="text-base font-medium text-[#1C1917] mt-2">
                    {formatPrice(product.price)}
                  </p>
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;
